import type { UseFetchOptions } from '#app'

interface Token {
  access: string
  refresh: string
}

interface Endpoints {
  baseURL?: string
  login: string
  logout?: string
  refresh: string
  me?: string
}

export const useAuth = (strategy = 'local') => {
  const user: Ref = useState(`auth:${strategy}:user`, () => null)
  const token: Ref<Token | null | any> = useState(`auth:${strategy}:token`, () => null)
  const status = useState(`auth:${strategy}:status`, () => 'unauthenticated')
  const config = useRuntimeConfig()
  const currentStrategy =
    config.public.auth?.strategies?.[strategy as keyof typeof config.public.auth.strategies] || config.public.auth
  const ENDPOINTS: Endpoints = currentStrategy?.endpoints
  const baseURL: string | undefined = ENDPOINTS?.baseURL

  function _fireHook(name: string) {
    const nuxt = useNuxtApp()
    // @ts-ignore
    nuxt.hooks.callHook(`auth:${strategy}:${name}`, status.value)
    // @ts-ignore
    nuxt.hooks.callHook(`auth:${name}`, { status: status.value, strategy })
  }

  function setCookie(name: string, value: string, expiration?: number) {
    const d = new Date()
    d.setTime(d.getTime() + (expiration || 0))
    const expires = 'expires=' + new Date(d.getTime()).toUTCString()
    document.cookie = name + '=' + value + ';' + (expiration ? expires : '') + ';path=/'
  }

  function getCookie(name: string) {
    const decodedCookie = decodeURIComponent(document.cookie)
    const ca = decodedCookie.split(';')
    name += '='
    for (let i = 0; i < ca.length; i++) {
      let c = ca[i]
      while (c.charAt(0) == ' ') {
        c = c.substring(1)
      }
      if (c.indexOf(name) == 0) {
        return c.substring(name.length, c.length)
      }
    }
    return ''
  }

  function removeCookie(name: string) {
    document.cookie = name + '=; Path=/; Expires=Thu, 01 Jan 1970 00:00:01 GMT;'
  }

  function getToken() {
    for (const key of ['access', 'refresh']) {
      const _token = getCookie(`auth:${strategy}:token:${key}`)
      if (_token) {
        token.value = {
          ...(token.value || {}),
          [key]: _token || null
        }
      }
    }

    return token.value || null
  }

  function getRefreshToken() {
    return new Promise(async (resolve, reject) => {
      try {
        const response = await useAuthFetch(ENDPOINTS.refresh, {
          baseURL,
          method: 'POST',
          strategy,
          body: { [currentStrategy?.token?.refresh?.key || 'refresh']: token.value?.refresh }
        })

        setToken(response)
        resolve(token.value)
      } catch (error) {
        token.value = null
        reject(error)
      }
    })
  }

  function setToken(data: any) {
    if (token.value === null) token.value = {}

    for (const key of ['access', 'refresh']) {
      // @ts-ignore
      const _token = currentStrategy?.token?.[key]
      if(data[_token.key]) {
        token.value[key] = data[_token.key]
        setCookie(`auth:${strategy}:token:${key}`, token.value[key], _token?.expires)
      }
    }
  }

  async function login(body: { email?: string; username?: string; password?: string } = {}, options?: any) {
    return new Promise(async (resolve, reject) => {
      try {
        let response = null;
        status.value = 'loading'
        _fireHook('status')
        getToken()
        if (!token.value) {
          try {
            response = await $fetch(ENDPOINTS.login, {
              ...options,
              baseURL,
              method: 'POST',
              body
            })

            setToken(response)
            getToken()
          } catch (error) {
            logout(false)
            reject(error)
          }
        }

        if (token.value) {
          status.value = 'authenticated'
          _fireHook('status')
          resolve(await getUser())
        }
      } catch (error) {
        logout()
        reject(error)
      }
    })
  }

  function getUser() {
    return new Promise(async (resolve, reject) => {
      if (ENDPOINTS.me) {
        try {
          user.value = await useAuthFetch(ENDPOINTS.me, { strategy })
          resolve(user.value)
        } catch (error) {
          reject(error)
        }
      } else resolve(null)
    })
  }

  function logout(trigger = true) {
    user.value = null
    token.value = null
    status.value = 'unauthenticated'
    _fireHook('status')

    for (let key of ['access', 'refresh']) {
      removeCookie(`auth:${strategy}:token:${key}`)
    }

    if(trigger) {
      _fireHook('logout')

      // @ts-ignore
      if (currentStrategy.loginURL) return navigateTo(currentStrategy.loginURL)
    }
  }

  async function middleware() {
    if (import.meta.client) {
      if (status.value === 'unauthenticated' && getToken()) await login()
      if (status.value === 'unauthenticated') logout()
    }
  }

  function hasRole(roles: string[]) {
    return user.value?.groups?.some((i: any) => roles.includes(i.name))
  }

  return {
    user,
    token,
    status,
    currentStrategy,

    login,
    logout,

    getUser,
    getToken,
    setToken,
    hasRole,
    getRefreshToken,

    middleware
  }
}
